import { Script } from "gatsby"
import { useEffect, useMemo, useState, FC, isValidElement } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useGateValue, useStatsigClient } from "@statsig/react-bindings"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import { renderToStaticMarkup } from "react-dom/server"
import { INLINES } from "@contentful/rich-text-types"

// utils
import { events } from "../../utils/heap/tracking_events"
import { getFlag } from "../../utils/query_param_flags"
import { renderRichText } from "../../utils/contentful/render_rich_text"
import { extractLangCode } from "../../utils/general_utils"

// types
import { RootState } from "../../store/store"
import { DuetMetaData } from "../../types/decagon.duet.type"
import { DuetLocalizationData } from "../../types/browser"

// redux slices
import { userProfileGuardCheck } from "../../store/slices/user_profile.slice"
import { setMetaData } from "../../store/slices/metadata.slice"

// components
import { goToLoginPage } from "../ProfileMenuBar/ProfileMenuBar"

// styles
import * as styles from "./ChatBot.module.scss"

const SCRIPT_SRC = process.env.GATSBY_CHATBOT_SCRIPTS
  ? typeof process.env.GATSBY_CHATBOT_SCRIPTS === "string"
    ? process.env.GATSBY_CHATBOT_SCRIPTS
    : ""
  : ""

if (SCRIPT_SRC?.length <= 0) {
  throw new Error("GATSBY_CHATBOT_SCRIPTS length <= 0")
}

// Statsig gate controlling the chatbot anon access rollout.
const ANON_ACCESS_GATE = "help_center_chatbot_anon_access"

const GATE_RESOLVE_TIMEOUT_MS = 2000

export const ChatBot: FC<{
  chatbotUITranslations: Queries.HomepageDataQuery["chatbotUITranslations"]
  onChatbotClick: () => void
}> = ({ chatbotUITranslations, onChatbotClick }) => {
  const [isMounted, setIsMounted] = useState(false)
  const [duetScriptLoaded, setDuetScriptLoaded] = useState(false)
  const [hasFiredDuetMetadata, setHasFiredDuetMetadata] = useState(false)
  const [duetIframe, setDuetIframe] = useState<HTMLIFrameElement>()
  const [isDuetReady, setIsDuetReady] = useState(false)
  const [hasSetUserAuth, setHasSetUserAuth] = useState(false)

  const dispatch = useDispatch()

  const defaultIframeDimensions = { height: "700px", width: "500px" }

  const urlParamFlag = getFlag()

  const chatbotUITranslationsMicropy = useMemo<DuetLocalizationData>(() => {
    const finalTranslationData: DuetLocalizationData = {
      widgetText: "Ask a question",
      chatTitle: "Chat with Eventbrite AI",
      waitTime: "Less than a minute",
      userTypePrompt: "I am...",
      userTypes: {
        attendee: "Attending an event",
        organizer: "Organizing an event"
      },
      conversationHistoryTitle: "My conversations",
      newConversation: "Send us a new message",
      noConversations: "No conversations yet",
      disclaimerHTML:
        '<p>Chat sessions are powered and recorded by Eventbrite and our vendors. By chatting, you agree to our <a href="https://www.eventbrite.com/help/en-us/articles/251210/eventbrite-terms-of-service/" target="_blank" class="text-blue-200 font-bold">Terms</a> and <a href="https://www.eventbrite.com/help/en-us/articles/460838/eventbrite-privacy-policy/" target="_blank" class="text-blue-200 font-bold">Privacy Policy</a>. This site is protected by reCAPTCHA.</p>',
      inputPlaceholder: "Ask a detailed question...",
      readMore: "read more"
    }

    if (chatbotUITranslations.edges?.length > 0) {
      chatbotUITranslations.edges[0].node?.microcopySetEntries?.forEach((y) => {
        if (
          y &&
          "microcopyKey" in y &&
          y.microcopyValue?.raw &&
          typeof y.microcopyKey === "string"
        ) {
          const parsedMicrocopyValue = JSON.parse(y.microcopyValue.raw)
          const value = documentToPlainTextString(parsedMicrocopyValue)

          if (y.microcopyKey?.trim() === "selectionAttendingAnEvent") {
            finalTranslationData.userTypes["attendee"] = value
          } else if (y.microcopyKey?.trim() === "selectionOrganizingAnEvent") {
            finalTranslationData.userTypes["organizer"] =
              documentToPlainTextString(JSON.parse(y.microcopyValue.raw))
          } else if (y.microcopyKey?.trim() === "disclaimerHTML") {
            const renderedRichText = renderRichText(y.microcopyValue.raw, {
              renderNode: {
                [INLINES.HYPERLINK]: (node, children) => {
                  const uri = node.data?.uri as string

                  return (
                    <a
                      href={uri}
                      target="_blank"
                      className="text-blue-200 font-bold"
                    >
                      {children}
                    </a>
                  )
                }
              }
            })

            if (renderedRichText && isValidElement(renderedRichText)) {
              finalTranslationData[y.microcopyKey] =
                renderToStaticMarkup(renderedRichText)
            }
          } else {
            finalTranslationData[y.microcopyKey] = documentToPlainTextString(
              JSON.parse(y.microcopyValue.raw)
            )
          }
        }
      })
    }

    return finalTranslationData
  }, [chatbotUITranslations])

  const logInToChatCopy = useMemo<string>(() => {
    let copy = "Log in to chat"

    chatbotUITranslations.edges?.[0]?.node?.microcopySetEntries?.forEach(
      (y) => {
        if (
          y &&
          "microcopyKey" in y &&
          y.microcopyKey?.trim() === "widgetLogInToChat" &&
          y.microcopyValue?.raw
        ) {
          const value = documentToPlainTextString(
            JSON.parse(y.microcopyValue.raw)
          )

          if (value?.length > 0) {
            copy = value
          }
        }
      }
    )

    return copy
  }, [chatbotUITranslations])

  const langCode =
    useSelector((state: RootState) => state.metaData.langCode) ||
    extractLangCode()
  const userProfile = userProfileGuardCheck(
    useSelector((state: RootState) => state.userProfile)
  )

  const chatbotApiCallsCompleted = useSelector(
    (state: RootState) => state.metaData.chatbotApiCallsCompleted
  )

  // The AI chatbot iframe is always injected, but its visibility is controlled
  // via the `shouldShowAIChatbot` Redux flag. This replaces older logic that
  // inferred visibility from a combination of live chat state flags.
  const shouldRenderBot = useSelector((state: RootState) => {
    return state.metaData.shouldShowAIChatbot
  })

  // Auth gating for anonymous users.
  // Anonymous access is being reopened gradually now that Decagon verifies
  // widget opens with reCAPTCHA. The Statsig gate is the rollout dial. GATSBY_ENABLE_CHATBOT
  //  is retained as a break-glass override for when Statsig itself is
  // unavailable — "true" keeps the bot visible to everyone regardless of the gate.
  // With neither set, the bot is gated to logged-in users only.
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )
  const isLoggedIn = useSelector((state: RootState) => state?.userProfile)
    ? true
    : false
  const anonAccessGate = useGateValue(ANON_ACCESS_GATE)
  const showChatbotForAnon =
    process.env.GATSBY_ENABLE_CHATBOT === "true" || anonAccessGate
  const passesAuthGate = showChatbotForAnon || (isLoggedIn && hasAttemptedLogin)

  const { client: statsigClient, isLoading: statsigIsLoading } =
    useStatsigClient()
  const [gateWaitElapsed, setGateWaitElapsed] = useState(false)

  useEffect(() => {
    const timer = setTimeout(
      () => setGateWaitElapsed(true),
      GATE_RESOLVE_TIMEOUT_MS
    )
    return () => clearTimeout(timer)
  }, [])

  const gateResolved =
    "isNoop" in statsigClient || !statsigIsLoading || gateWaitElapsed

  const showLoginPrompt =
    shouldRenderBot &&
    !passesAuthGate &&
    hasAttemptedLogin &&
    !isLoggedIn &&
    gateResolved

  useEffect(() => {
    if (isDuetReady) {
      window?.duet?.setLocalizationData(chatbotUITranslationsMicropy)
    }
  }, [isDuetReady, duetIframe, chatbotUITranslationsMicropy])

  useEffect(() => {
    if (typeof window === "undefined") return

    // Function to check for the object
    const checkForDuet = () => {
      if (window.duet?.isReady() && !isDuetReady) {
        setIsDuetReady(true)
      }
    }

    checkForDuet()

    const intervalId = setInterval(checkForDuet, 100)

    if (isDuetReady) {
      clearInterval(intervalId)
    }

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId)
  }, [isDuetReady])

  useEffect(() => {
    if (isDuetReady && !hasSetUserAuth) {
      process.env.GATSBY_DEBUG === "true" &&
        console.log("Setting user authentication before Duet initializes")
      onChatbotClick()
      setHasSetUserAuth(true)
    }
  }, [isDuetReady, hasSetUserAuth, onChatbotClick])

  useEffect(() => {
    if (typeof window === "undefined") return

    if (!duetIframe) {
      Object.values(document.getElementsByTagName("iframe")).forEach((x) => {
        if (x.src.includes("decagon")) {
          x.style.display = "none"
          setDuetIframe(x)
        }
      })
    }

    if (duetIframe) {
      process.env.GATSBY_DEBUG === "true" &&
        console.log("ChatBot widget visibility debug:", {
          shouldRenderBot,
          chatbotApiCallsCompleted,
          passesAuthGate
        })
      if (shouldRenderBot && chatbotApiCallsCompleted && passesAuthGate) {
        duetIframe.style.display = "block"
      } else {
        duetIframe.style.display = "none"
      }
    }
  })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return null
  }

  return (
    <>
      {showLoginPrompt && (
        <button
          className={styles?.loginPrompt}
          onClick={() => goToLoginPage()}
          data-heap-id="button.chatbot.log-in-to-chat"
          data-testid="chatbot-login-prompt"
        >
          {/* Chat bubble icon, mirroring the Decagon widget button */}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 3C6.48 3 2 6.94 2 11.8c0 2.15.88 4.12 2.34 5.65-.18 1.18-.65 2.34-1.4 3.28-.16.2-.02.5.23.48 1.66-.11 3.24-.72 4.5-1.6 1.3.5 2.75.79 4.33.79 5.52 0 10-3.94 10-8.8S17.52 3 12 3zm-4.5 9.9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
          </svg>
          {logInToChatCopy}
        </button>
      )}
      {shouldRenderBot && passesAuthGate && (
        <a className={styles?.chatbotContainer}>
          {SCRIPT_SRC && langCode && (
            <Script
              id="chatBot"
              type="module"
              src={SCRIPT_SRC}
              onLoad={(ev) => {
                process.env.GATSBY_DEBUG === "true" &&
                  console.log("Chatbot script loaded")

                window?.duet?.setEventListener((event) => {
                  if (event?.eventName?.length > 0) {
                    process.env.GATSBY_DEBUG === "true" &&
                      console.log("Duet event:", event)

                    events?.duetEvent(event?.eventName)

                    // events are now placed in order in which they happen
                    if (event.eventName === "DECAGON_LOADED") {
                      onChatbotClick()
                      dispatch(setMetaData({ decagonLoadedEventFired: true }))
                    }

                    // Clear channel_selected when a new conversation loads so it doesn't carry over.
                    // Covers both first open (DUET_INITIALIZE) and "new conversation" in same session (CONVERSATION_LOADED).
                    if (
                      event.eventName === "DUET_INITIALIZE" ||
                      event.eventName === "CONVERSATION_LOADED"
                    ) {
                      if (window?.duet?.addMetadata) {
                        window.duet.addMetadata({ channel_selected: null })
                        process.env.GATSBY_DEBUG === "true" &&
                          console.log(
                            "[ChatBot] Cleared channel_selected on conversation load:",
                            event.eventName
                          )
                      }
                    }

                    if (event.eventName === "DUET_INITIALIZE") {
                      process.env.GATSBY_DEBUG === "true" &&
                        console.log("Attempting to send duet langCode metadata")

                      if (!langCode || langCode === "UNKNOWN") {
                        console.warn(
                          "No langCode is available to send for chatbot",
                          { langCode }
                        )
                      }

                      // set metadata if eventname is DUET_INITIALIZE
                      if (langCode && !hasFiredDuetMetadata) {
                        const metaDataObj = {} as DuetMetaData

                        if (!langCode || langCode === "UNKNOWN") {
                          metaDataObj.langCode = extractLangCode() || ""
                        }

                        if (window?.location) {
                          metaDataObj.path = window.location.pathname
                          metaDataObj.url = window.location.href
                        }

                        if (window?.location) {
                          if (window.location?.pathname) {
                            process.env.GATSBY_DEBUG === "true" &&
                              console.log({
                                updated_url: window.location.pathname
                              })

                            metaDataObj.updated_url = window.location.pathname
                          }
                        }

                        metaDataObj.langCode = langCode

                        // Check for decagonEscalation query parameter
                        if (typeof window !== "undefined" && window.location) {
                          const queryParams = new URLSearchParams(
                            window.location.search
                          )
                          const decagonEscalation =
                            queryParams.get("decagonEscalation")

                          if (decagonEscalation === "true") {
                            metaDataObj.Escalation_enabled = true
                            process.env.GATSBY_DEBUG === "true" &&
                              console.log(
                                "[Decagon Escalation from Chatbot.tsx] ✅ Escalation_enabled set to TRUE in metadata"
                              )
                          }
                        }
                        process.env.GATSBY_DEBUG === "true" &&
                          console.log("Duet metadata object: ", metaDataObj)

                        if (window?.duet?.addMetadata) {
                          window?.duet?.addMetadata(metaDataObj)
                        }

                        process.env.GATSBY_DEBUG === "true" &&
                          console.log("Finished sending metadata to duet")

                        setHasFiredDuetMetadata(true)
                        if (!hasSetUserAuth) {
                          process.env.GATSBY_DEBUG === "true" &&
                            console.log(
                              "Calling onChatbotClick to set user auth"
                            )
                          onChatbotClick()
                          setHasSetUserAuth(true)
                        }
                      }
                    }

                    // this event happens when a user has sent a message and received a message back from the chatbot
                    if (event?.eventName === "DUET_RESPONSE") {
                      dispatch(setMetaData({ shouldPollUserMetadata: true }))
                    }
                  }
                })

                setDuetScriptLoaded(true)
              }}
            />
          )}
        </a>
      )}
    </>
  )
}
